import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import styled, { css } from 'styled-components';

import { ErrorMessage as AkErrorMessage } from '@atlaskit/form/error-message';
import AkHeading from '@atlaskit/heading/heading';
import AkWarningIcon from '@atlaskit/icon/core/status-warning';
import InlineDialog from '@atlaskit/inline-dialog/inline-dialog';
import AkInlineEdit from '@atlaskit/inline-edit/inline-edit';
import { Box as AkBox } from '@atlaskit/primitives/box';
import AkTextfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

import type { ExtendedFieldProps as AkExtendedFieldProps } from '@adminhub/types/inline-edit';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const Title = styled.span`
	font-size: ${token('font.heading.small')};
	font-weight: ${token('font.weight.semibold')};
	padding-top: ${'3'}px;
	margin-top: 0;
	${({ isDisabled }: { isDisabled: boolean }) =>
		isDisabled
			? css`
					color: ${token('color.text.disabled')};
				`
			: ''};
`;

interface TextfieldWrapperProps {
	isCompact: boolean;
}

const compactInputStyles = css`
	/* Give the compact edit field room to grow so the title isn't clipped
	   mid-word while editing (the read view is content-sized in the toolbar,
	   which would otherwise leave the input very narrow). */
	min-width: 320px;

	input {
		font-size: ${16 / 14}em;
		line-height: ${20 / 16};
		font-weight: ${token('font.weight.semibold')};
	}
`;

const defaultInputStyles = css`
	input {
		font-size: ${24 / 14}em;
		line-height: ${28 / 24};
		font-weight: ${token('font.weight.medium')};
	}
`;

const TextfieldWrapper = styled.div<TextfieldWrapperProps>`
	input {
		color: ${token('color.text')};
		letter-spacing: -0.01em;
		margin: 0;
	}
	${(props: TextfieldWrapperProps) => (props.isCompact ? compactInputStyles : defaultInputStyles)};
`;

const ErrorWrapper = styled.div`
	margin: 0;
	font-size: initial;
`;

const WarningIconWrapper = styled.div`
	padding-right: ${token('space.100')};
	line-height: 100%;
`;

const InlineEditWrapper = styled.div`
	margin-top: ${token('space.negative.100')};
	width: 100%;

	#error-message {
		font-size: ${token('font.body')};
	}
`;

const ReadViewWrapper = styled.div`
	margin: 0 0 0 ${token('space.negative.025')};
`;

/**
 * HTML element / heading level accepted by the inline-edit title, derived from
 * `@atlaskit/heading` so consumers (e.g. MainLayout) stay in sync if ADS changes
 * its `as` union rather than duplicating the literal type locally.
 */
export type InlineEditTitleAs = React.ComponentProps<typeof AkHeading>['as'];

interface OwnProps {
	defaultValue: string;
	errorMessage?: string | null;
	onConfirm?(value: string): void;
	/**
	 * Size of the read-view heading. Defaults to 'large' to preserve the original
	 * page-title styling; pass a smaller size when the title sits inside a compact
	 * toolbar rather than a full page header.
	 */
	size?: React.ComponentProps<typeof AkHeading>['size'];
	/**
	 * HTML element / heading level for the read-view heading. When omitted, the level
	 * is inferred from `size` by `@atlaskit/heading` (e.g. 'large' → h2). Pass `as="h1"`
	 * when this title is the page's top-level heading so it doesn't skip a level.
	 */
	as?: InlineEditTitleAs;
}

export const InlineEditTitle: React.FC<OwnProps> = ({
	defaultValue,
	errorMessage,
	onConfirm,
	size = 'large',
	as,
}) => {
	const [inlineTitleTextValue, setInlineTitleTextValue] = React.useState<string>(defaultValue);

	React.useEffect(() => {
		setInlineTitleTextValue(defaultValue);
	}, [defaultValue]);

	const handleConfirm = (value: string) => {
		const trimmed = value.trim();
		if (!trimmed) {
			return;
		}
		setInlineTitleTextValue(trimmed);
		if (onConfirm) {
			onConfirm(trimmed);
		}
	};

	const validate = (value: string) => {
		if (errorMessage && value === inlineTitleTextValue) {
			return errorMessage;
		}

		return undefined;
	};

	const renderInlineEditView = ({
		errorMessage: _errorMessage,
		...fieldProps
	}: AkExtendedFieldProps<string>) => (
		<InlineDialog
			isOpen={fieldProps.isInvalid}
			content={<AkBox id="error-message">{errorMessage}</AkBox>}
			placement="right"
		>
			<TextfieldWrapper isCompact={size !== 'large'}>
				<AkTextfield
					{...fieldProps}
					autoFocus
					testId="inline-edit-edit-view"
					elemAfterInput={
						errorMessage &&
						fieldProps.value === inlineTitleTextValue && (
							<WarningIconWrapper>
								<AkWarningIcon
									label="information"
									color={token('color.icon.warning')}
									spacing="none"
								/>
							</WarningIconWrapper>
						)
					}
				/>
			</TextfieldWrapper>
		</InlineDialog>
	);

	const renderInlineEditReadView = () => (
		<AkHeading size={size} as={as}>
			<ReadViewWrapper data-testid="inline-edit-title-view">
				<Title isDisabled={false}>{inlineTitleTextValue}</Title>
				{errorMessage && (
					<ErrorWrapper>
						<AkErrorMessage>{errorMessage}</AkErrorMessage>
					</ErrorWrapper>
				)}
			</ReadViewWrapper>
		</AkHeading>
	);

	return (
		<InlineEditWrapper>
			<AkInlineEdit
				defaultValue={inlineTitleTextValue}
				editView={renderInlineEditView}
				readView={renderInlineEditReadView}
				onConfirm={handleConfirm}
				validate={validate}
				isRequired={true}
			/>
		</InlineEditWrapper>
	);
};
